import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@shadcn/ui/button";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { DrawerTrigger } from "@shadcn/ui/drawer";
import { useI18nContext } from "@i18n/i18n-react";
import { X } from "lucide-react";
import type { ReactNode } from "react";

type VideoGuideDrawerProps = {
	trigger: ReactNode;
};

const BACEHUB_URL = "https://bacehub.de";

export const VideoGuideDrawer = ({ trigger }: VideoGuideDrawerProps) => {
	return (
		<a href={BACEHUB_URL} className="block text-left">
			{trigger}
		</a>
	);
};


type GuideStep = {
	title: string;
	videoSrc: string;
	tryLabel: string;
	tryHref: string;
	guideLabel: string;
	guideTo: string;
};

const VideoGuideDrawerOld = (props: VideoGuideDrawerProps) => {
	const { trigger } = props;
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const activeBarRef = useRef<HTMLDivElement | null>(null);
	const animationRef = useRef<Animation | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [progress, setProgress] = useState([0, 0, 0]);
	const { LL } = useI18nContext();
	const i18n = LL["new-ui"];

	const steps = useMemo<GuideStep[]>(
		() => [
			{
				title: i18n.INSTANT_BUY_TITLE(),
				videoSrc: "/video-guide-1.mp4",
				tryLabel: i18n.VIDEO_GUIDE_TRY_INSTANT_BUY(),
				tryHref: "/#discover-instant-buy",
				guideLabel: i18n.VIDEO_GUIDE_INSTANT_BUY_GUIDE(),
				guideTo: "/support/how-to-use/instant-buy",
			},
			{
				title: i18n.CLICK_AND_COLLECT_TITLE(),
				videoSrc: "/video-guide-2.mp4",
				tryLabel: i18n.VIDEO_GUIDE_TRY_CLICK_COLLECT(),
				tryHref: "/#pickup-click-collect",
				guideLabel: i18n.VIDEO_GUIDE_CLICK_COLLECT_GUIDE(),
				guideTo: "/support/how-to-use/click-and-collect",
			},
			{
				title: i18n.SHOP_N_COLLECT_TITLE(),
				videoSrc: "/video-guide-3.mp4",
				tryLabel: i18n.VIDEO_GUIDE_TRY_SHOP_COLLECT(),
				tryHref: "/#discover-shop-collect",
				guideLabel: i18n.VIDEO_GUIDE_SHOP_COLLECT_GUIDE(),
				guideTo: "/support/how-to-use/shop-and-collect",
			},
		],
		[i18n]
	);

	const currentStep = steps[currentIndex] ?? steps[0];
	const stepCount = steps.length;

	const setStepProgress = useCallback(
		(activeIndex: number, activeProgress = 0) => {
			setProgress(
				Array.from({ length: stepCount }, (_, index) => {
					if (index < activeIndex) return 1;
					if (index > activeIndex) return 0;
					return activeProgress;
				})
			);
		},
		[stepCount]
	);

	const cancelProgressAnimation = useCallback(() => {
		animationRef.current?.cancel();
		animationRef.current = null;
	}, []);

	const handleOpenChange = useCallback(
		(open: boolean) => {
			setIsOpen(open);
			if (open) {
				setCurrentIndex(0);
				setStepProgress(0, 0);
				return;
			}
		},
		[cancelProgressAnimation, setStepProgress]
	);

	const syncProgressAnimation = useCallback(() => {
		const video = videoRef.current;
		const activeBar = activeBarRef.current;
		if (!video || !activeBar || !isOpen) return;

		const currentProgress = video.duration ? Math.min(video.currentTime / video.duration, 1) : 0;
		setStepProgress(currentIndex, currentProgress);
		cancelProgressAnimation();

		if (!video.duration || currentProgress >= 1) return;

		animationRef.current = activeBar.animate([{ transform: `scaleX(${currentProgress})` }, { transform: "scaleX(1)" }], {
			duration: (video.duration - video.currentTime) * 1000,
			easing: "linear",
			fill: "forwards",
		});

		if (video.paused) {
			animationRef.current.pause();
		}
	}, [cancelProgressAnimation, currentIndex, isOpen, setStepProgress]);

	const handleTimeUpdate = useCallback(() => {
		const video = videoRef.current;
		if (!video || !isOpen) return;

		const currentProgress = video.duration ? Math.min(video.currentTime / video.duration, 1) : 0;
		setStepProgress(currentIndex, currentProgress);
	}, [currentIndex, isOpen, setStepProgress]);

	useEffect(() => {
		if (!isOpen) return;
		const video = videoRef.current;
		if (!video) return;

		video.currentTime = 0;
		video.play().catch(() => { });
	}, [currentIndex, isOpen, videoRef.current, currentStep?.videoSrc, syncProgressAnimation]);

	if (!currentStep) {
		return null;
	}

	const handleEnded = () => {
		cancelProgressAnimation();
		setStepProgress(currentIndex, 1);
		setCurrentIndex((prev) => (prev < stepCount - 1 ? prev + 1 : prev));
	};

	const goToStep = (nextIndex: number) => {
		if (nextIndex < 0 || nextIndex >= stepCount) return;
		setProgress(
			Array.from({ length: stepCount }, (_, index) => {
				if (index < nextIndex) return 1;
				if (index > nextIndex) return 0;
				return 0;
			})
		);
		setCurrentIndex(nextIndex);
	};

	return (
		<KeyboardAwareDrawer shouldScaleBackground dismissible={false} open={isOpen} onOpenChange={handleOpenChange}>
			<DrawerTrigger className="text-left">{trigger}</DrawerTrigger>
			<KeyboardAwareDrawerContent
				hasHandle={false}
				className="overflow-hidden rounded-none border-0"
				style={{ top: 0, bottom: 0, height: "100dvh", maxHeight: "100dvh", marginTop: 0, borderRadius: 0 }}
			>
				<div className="relative h-svh overflow-hidden bg-black">
					<video
						ref={videoRef}
						key={currentStep.videoSrc}
						src={currentStep.videoSrc}
						className="absolute inset-0 size-full object-cover"
						autoPlay
						playsInline
						onTimeUpdate={handleTimeUpdate}
						onSeeking={syncProgressAnimation}
						onLoadedMetadata={syncProgressAnimation}
						onRateChange={syncProgressAnimation}
						onEnded={handleEnded}
					/>
					<div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/35" />
					<div
						className="relative flex h-full flex-col px-6 pb-6 pt-2"
						onClick={(e) => goToStep(currentIndex + (e.clientX >= screen.width / 2 ? 1 : -1))}
					>
						<div className="space-y-4">
							<div className="flex gap-2">
								{steps.map((step, index) => (
									<div key={step.title} className="h-1 flex-1 overflow-hidden rounded-full bg-white/50">
										<div
											ref={index === currentIndex ? activeBarRef : undefined}
											className="h-full origin-left bg-white"
											style={{ transform: `scaleX(${progress[index] ?? 0})` }}
										/>
									</div>
								))}
							</div>

							<div className="flex items-center gap-3">
								<h1 className="flex-1 font-breit text-lg leading-7 text-white">{currentStep.title}</h1>
								<Button
									variant="secondary"
									size="icon"
									className="shrink-0 bg-white/90 hover:bg-white"
									onClick={() => handleOpenChange(false)}
								>
									<X className="size-4" />
								</Button>
							</div>
						</div>

						<div className="mt-auto space-y-3">
							<Button className="h-11 w-full rounded-md bg-primary text-primary-foreground hover:bg-primary/95" asChild>
								<a href={currentStep.tryHref}>{currentStep.tryLabel}</a>
							</Button>
							<Button variant="outline" className="h-11 w-full rounded-md bg-white/90 text-foreground hover:bg-white" asChild>
								<Link to={currentStep.guideTo}>{currentStep.guideLabel}</Link>
							</Button>
						</div>
					</div>
				</div>
			</KeyboardAwareDrawerContent>
		</KeyboardAwareDrawer>
	);
};
